import {Component, Input, OnDestroy, OnInit, Type, ViewChild} from '@angular/core';
import {LoadExamPartResultDirective} from "../../directives/load-exam-part-result.directive";
import {CorResultComponent} from "../exam-part-results/cor-result/cor-result.component";
import {DictResultComponent} from "../exam-part-results/dict-result/dict-result.component";
import {FillMultiResultComponent} from "../exam-part-results/fill-multi-result/fill-multi-result.component";
import {PicturesResultComponent} from "../exam-part-results/pictures-result/pictures-result.component";
import {JumbleResultComponent} from "../exam-part-results/jumble-result/jumble-result.component";
import {AudioResultComponent} from "../exam-part-results/audio-result/audio-result.component";
import {V3ExamSessionPart, V3ExerciseSessionQuestion} from "../../../cspa/model/v3/cspa";
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {Subscription, switchMap} from "rxjs";

@Component({
    selector: 'app-exam-part-result',
    templateUrl: './exam-part-result.component.html',
    styleUrls: ['./exam-part-result.component.scss'],
    standalone: false
})
export class ExamPartResultComponent implements OnInit, OnDestroy {
  @ViewChild(LoadExamPartResultDirective, {static: true}) loadExamPartResultDirective!: LoadExamPartResultDirective

  private _examPart: V3ExamSessionPart
  private _lastLoadKey: string | null = null
  private questionsSub?: Subscription
  private _personId!: number
  private _examSessionUuid!: string
  loadingQuestions = false
  private readonly resultComponentsByType: Record<string, Type<any>> = {
    V3Correction: CorResultComponent,
    V3Dictation: DictResultComponent,
    V3FillMulti: FillMultiResultComponent,
    V3Images: PicturesResultComponent,
    V3Jumble: JumbleResultComponent,
    V3Audio: AudioResultComponent,
  }

  @Input() set personId(value: number) {
    this._personId = value
    this.tryLoadQuestions()
  }

  get personId(): number {
    return this._personId
  }

  @Input() set examSessionUuid(value: string) {
    this._examSessionUuid = value
    this.tryLoadQuestions()
  }

  get examSessionUuid(): string {
    return this._examSessionUuid
  }

  @Input() set examPart(part: V3ExamSessionPart) {
    if (!part) return
    this._examPart = part
    this.tryLoadQuestions()
  }

  get examPart(): V3ExamSessionPart {
    return this._examPart
  }

  constructor(
    private provider: NativeServiceApiProvider,
  ) {
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.questionsSub?.unsubscribe()
  }

  private tryLoadQuestions() {
    if (!this._examPart || !this.personId || !this.examSessionUuid) {
      return
    }
    const loadKey = `${this.personId}:${this.examSessionUuid}:${this._examPart.num}`
    if (this._lastLoadKey === loadKey) {
      return
    }
    this._lastLoadKey = loadKey
    this.questionsSub?.unsubscribe()
    this.loadingQuestions = true
    this.questionsSub = this.provider.cspaV3().pipe(
      switchMap(api => api.getPartQuestions(this.personId, this.examSessionUuid, this._examPart.num))
    ).subscribe({
      next: questions => {
        this.onLoadQuestion(questions)
        this.loadingQuestions = false
      },
      error: _ => {
        this.onLoadQuestion([])
        this.loadingQuestions = false
      },
    })
  }

  onLoadQuestion(questions: V3ExerciseSessionQuestion[]) {
    this.loadExamPartResultDirective.viewContainerRef.clear();
    const examPartTypes = Array.from(
      new Set(questions.map(question => question?.questionDef?.['@type']).filter(Boolean)),
    ) as string[]

    examPartTypes.forEach(type => {
      const componentType = this.resultComponentsByType[type]
      if (!componentType) {
        return
      }
      const componentRef = this.loadExamPartResultDirective.viewContainerRef.createComponent(componentType)
      componentRef.instance.questions = questions.filter(q => q?.questionDef?.['@type'] === type)
    })
  }
}
